import { useState, useRef, useCallback, useMemo } from 'react'
import { useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'
import { Play, Square, Loader2, Search, Sliders, Volume2, Mic, Zap, ChevronDown, ChevronUp } from 'lucide-react'

// All v3 emphasis/direction tags organized by category
const TAG_CATEGORIES = [
  {
    label: 'Emotion',
    tags: ['sad', 'angry', 'happily', 'excited', 'nervous', 'frustrated', 'calm', 'cheerfully', 'deadpan', 'playfully', 'annoyed', 'curious', 'mischievously', 'panic'],
  },
  {
    label: 'Tone',
    tags: ['dramatic tone', 'wistful', 'serious tone', 'matter-of-fact', 'conversational tone', 'sarcastic', 'awe', 'lighthearted', 'reflective', 'resigned'],
  },
  {
    label: 'Reactions',
    tags: ['laughs', 'laughs softly', 'giggles', 'nervous laugh', 'sighs', 'gasps', 'clears throat', 'crying', 'exhales'],
  },
  {
    label: 'Delivery',
    tags: ['whispers', 'whispering', 'quietly', 'loudly', 'shouts', 'rushed', 'slows down', 'pause', 'hesitates', 'stammers', 'singing'],
  },
  {
    label: 'Character',
    tags: ['deep voice', 'robotic', 'pirate voice', 'childlike tone', 'fantasy narrator', 'sci-fi AI voice'],
  },
  {
    label: 'Accents',
    tags: ['French accent', 'British accent', 'Australian accent', 'Southern US accent', 'Irish accent', 'Scottish accent'],
  },
]

const DEFAULT_TEXT = `This is a normal speaking voice to start with.

[whispers] But sometimes we need to get really quiet, like we're sharing a secret.

[excited] And then suddenly the energy picks up and everything feels electric!

[sighs] Though eventually... the moment passes. [pause] And we're left with the silence.

[dramatic tone] In the beginning, there was nothing. Then, a spark — a SINGLE moment that changed EVERYTHING.

[laughs softly] Of course, I'm being a bit dramatic. [conversational tone] The truth is much simpler than that.

[sad] Some stories don't have happy endings. That's just... how it goes sometimes.

[angry] But we don't have to ACCEPT that! We can fight for something better!

[calm] Take a deep breath. Center yourself. [slows down] Let each word... land... with intention.

[whispers] One last thing before I go — [gasps] wait, did you hear that?`

const MODELS = [
  { id: 'eleven_v3', label: 'Eleven v3 (best quality)' },
  { id: 'eleven_multilingual_v2', label: 'Multilingual v2' },
  { id: 'eleven_flash_v2_5', label: 'Flash v2.5 (fastest)' },
]

const OUTPUT_FORMATS = [
  { id: 'mp3_44100_192', label: 'MP3 192kbps (highest)' },
  { id: 'mp3_44100_128', label: 'MP3 128kbps (default)' },
  { id: 'mp3_44100_96', label: 'MP3 96kbps' },
  { id: 'mp3_44100_64', label: 'MP3 64kbps' },
]

interface Voice {
  voice_id: string
  name: string
  category: string
  labels: Record<string, string>
  preview_url: string
  high_quality_base_model_ids: string[]
}

interface GenerationResult {
  voiceName: string
  voiceId: string
  params: {
    model: string
    stability: number
    similarityBoost: number
    style: number
    useSpeakerBoost: boolean
    speed: number
    outputFormat: string
  }
  audioDataUrl: string
  timestamp: number
}

export default function AdminVoiceLabPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin

  const listVoices = useAction(api.elevenlabs.listVoices)
  const generateTTS = useAction(api.elevenlabs.generateTTS)

  // Voice list state
  const [voices, setVoices] = useState<Voice[]>([])
  const [voicesLoading, setVoicesLoading] = useState(false)
  const [voicesLoaded, setVoicesLoaded] = useState(false)
  const [voiceSearch, setVoiceSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [genderFilter, setGenderFilter] = useState<string>('all')

  // Selected voice & generation
  const [selectedVoice, setSelectedVoice] = useState<Voice | null>(null)
  const [text, setText] = useState(DEFAULT_TEXT)
  const [model, setModel] = useState('eleven_v3')
  const [stability, setStability] = useState(0.5)
  const [similarityBoost, setSimilarityBoost] = useState(0.75)
  const [style, setStyle] = useState(0)
  const [useSpeakerBoost, setUseSpeakerBoost] = useState(true)
  const [speed, setSpeed] = useState(1.0)
  const [outputFormat, setOutputFormat] = useState('mp3_44100_192')
  const [generating, setGenerating] = useState(false)

  // Results history
  const [results, setResults] = useState<GenerationResult[]>([])

  // Audio playback
  const [playingIdx, setPlayingIdx] = useState<number | null>(null)
  const [previewPlaying, setPreviewPlaying] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const previewAudioRef = useRef<HTMLAudioElement | null>(null)

  // Tag picker
  const [showTags, setShowTags] = useState(false)

  const loadVoices = useCallback(async () => {
    setVoicesLoading(true)
    try {
      const v = await listVoices()
      setVoices(v)
      setVoicesLoaded(true)
    } catch (e: any) {
      console.error('Failed to load voices:', e)
    } finally {
      setVoicesLoading(false)
    }
  }, [listVoices])

  const filteredVoices = useMemo(() => {
    let result = voices
    if (voiceSearch) {
      const q = voiceSearch.toLowerCase()
      result = result.filter(v =>
        v.name.toLowerCase().includes(q) ||
        (v.labels?.accent || '').toLowerCase().includes(q) ||
        (v.labels?.descriptive || '').toLowerCase().includes(q) ||
        (v.labels?.use_case || '').toLowerCase().includes(q)
      )
    }
    if (categoryFilter !== 'all') {
      result = result.filter(v => v.category === categoryFilter)
    }
    if (genderFilter !== 'all') {
      result = result.filter(v => (v.labels?.gender || '') === genderFilter)
    }
    return result
  }, [voices, voiceSearch, categoryFilter, genderFilter])

  const categories = useMemo(() => {
    const cats = new Set(voices.map(v => v.category))
    return ['all', ...Array.from(cats).sort()]
  }, [voices])

  const handleGenerate = useCallback(async () => {
    if (!selectedVoice || !text.trim()) return
    setGenerating(true)
    try {
      const result = await generateTTS({
        text: text.trim(),
        voiceId: selectedVoice.voice_id,
        modelId: model,
        stability,
        similarityBoost,
        style,
        useSpeakerBoost,
        speed,
        outputFormat,
      })
      setResults(prev => [{
        voiceName: selectedVoice.name,
        voiceId: selectedVoice.voice_id,
        params: { model, stability, similarityBoost, style, useSpeakerBoost, speed, outputFormat },
        audioDataUrl: result.audioDataUrl,
        timestamp: Date.now(),
      }, ...prev])
    } catch (e: any) {
      console.error('Generation failed:', e)
      alert(`Generation failed: ${e.message}`)
    } finally {
      setGenerating(false)
    }
  }, [selectedVoice, text, model, stability, similarityBoost, style, useSpeakerBoost, speed, outputFormat, generateTTS])

  const playResult = useCallback((idx: number) => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    if (playingIdx === idx) {
      setPlayingIdx(null)
      return
    }
    const audio = new Audio(results[idx].audioDataUrl)
    audio.onended = () => setPlayingIdx(null)
    audio.play()
    audioRef.current = audio
    setPlayingIdx(idx)
  }, [playingIdx, results])

  const playPreview = useCallback((voiceId: string, previewUrl: string) => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause()
      previewAudioRef.current = null
    }
    if (previewPlaying === voiceId) {
      setPreviewPlaying(null)
      return
    }
    const audio = new Audio(previewUrl)
    audio.onended = () => setPreviewPlaying(null)
    audio.play()
    previewAudioRef.current = audio
    setPreviewPlaying(voiceId)
  }, [previewPlaying])

  const insertTag = useCallback((tag: string) => {
    setText(prev => prev + `[${tag}] `)
  }, [])

  if (!isAdmin) {
    return <div className="p-6 text-neutral-500">Admin access required</div>
  }

  return (
    <div className="p-6 space-y-6 max-w-[1400px]">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Voice Lab</h1>
        <p className="text-sm text-neutral-500">ElevenLabs direct API — test voices and parameters</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* Left: Voice Browser */}
        <div className="space-y-4">
          <div className="bg-white border border-neutral-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-neutral-700 flex items-center gap-1.5">
                <Mic className="h-4 w-4" />
                Voices
              </h2>
              {!voicesLoaded && (
                <button
                  onClick={loadVoices}
                  disabled={voicesLoading}
                  className="text-xs px-3 py-1.5 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 disabled:opacity-50 flex items-center gap-1.5"
                >
                  {voicesLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                  Load voices
                </button>
              )}
            </div>

            {voicesLoaded && (
              <>
                <div className="relative mb-3">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
                  <input
                    type="text"
                    value={voiceSearch}
                    onChange={e => setVoiceSearch(e.target.value)}
                    placeholder="Search voices..."
                    className="w-full pl-8 pr-3 py-2 text-sm border border-neutral-200 rounded-lg bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                  />
                </div>

                <div className="flex gap-2 mb-3">
                  <select
                    value={categoryFilter}
                    onChange={e => setCategoryFilter(e.target.value)}
                    className="flex-1 text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-neutral-50"
                  >
                    {categories.map(c => (
                      <option key={c} value={c}>{c === 'all' ? 'All types' : c}</option>
                    ))}
                  </select>
                  <select
                    value={genderFilter}
                    onChange={e => setGenderFilter(e.target.value)}
                    className="flex-1 text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-neutral-50"
                  >
                    <option value="all">All genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="neutral">Neutral</option>
                  </select>
                </div>

                <div className="text-xs text-neutral-400 mb-2">{filteredVoices.length} voices</div>
              </>
            )}

            <div className="max-h-[500px] overflow-y-auto space-y-1 -mx-1 px-1">
              {filteredVoices.map(v => (
                <button
                  key={v.voice_id}
                  onClick={() => setSelectedVoice(v)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 rounded-lg transition-colors group',
                    selectedVoice?.voice_id === v.voice_id
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-neutral-50 border border-transparent'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-neutral-900 truncate">{v.name}</div>
                      <div className="text-xs text-neutral-500 truncate mt-0.5">
                        {[v.labels?.gender, v.labels?.age, v.labels?.accent].filter(Boolean).join(' · ')}
                      </div>
                      {v.labels?.descriptive && (
                        <div className="text-xs text-neutral-400 truncate">{v.labels.descriptive}</div>
                      )}
                    </div>
                    {v.preview_url && (
                      <button
                        onClick={e => { e.stopPropagation(); playPreview(v.voice_id, v.preview_url) }}
                        className="ml-2 p-1.5 rounded-md hover:bg-neutral-200 text-neutral-400 hover:text-neutral-700 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        {previewPlaying === v.voice_id ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                  {v.labels?.use_case && (
                    <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 bg-neutral-100 text-neutral-500 rounded">
                      {v.labels.use_case.replace(/_/g, ' ')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Generator */}
        <div className="space-y-4">
          {/* Selected voice header */}
          {selectedVoice && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center justify-between">
              <div>
                <span className="text-sm font-semibold text-blue-900">{selectedVoice.name}</span>
                <span className="text-xs text-blue-600 ml-2">
                  {[selectedVoice.labels?.gender, selectedVoice.labels?.accent, selectedVoice.labels?.descriptive].filter(Boolean).join(' · ')}
                </span>
              </div>
              <span className="text-[10px] font-mono text-blue-400">{selectedVoice.voice_id}</span>
            </div>
          )}

          {/* Text input */}
          <div className="bg-white border border-neutral-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-neutral-700">Text</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setText(DEFAULT_TEXT)}
                  className="text-[11px] text-neutral-400 hover:text-neutral-600"
                >
                  Reset to sample
                </button>
                <button
                  onClick={() => setShowTags(!showTags)}
                  className={cn(
                    "text-xs px-2 py-1 rounded-md border transition-colors flex items-center gap-1",
                    showTags ? "bg-violet-50 border-violet-200 text-violet-700" : "border-neutral-200 text-neutral-500 hover:text-neutral-700"
                  )}
                >
                  Tags {showTags ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>
              </div>
            </div>

            {showTags && (
              <div className="mb-3 p-3 bg-neutral-50 rounded-lg border border-neutral-100">
                {TAG_CATEGORIES.map(cat => (
                  <div key={cat.label} className="mb-2 last:mb-0">
                    <div className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-1">{cat.label}</div>
                    <div className="flex flex-wrap gap-1">
                      {cat.tags.map(tag => (
                        <button
                          key={tag}
                          onClick={() => insertTag(tag)}
                          className="text-[11px] px-1.5 py-0.5 bg-white border border-neutral-200 rounded hover:bg-violet-50 hover:border-violet-300 hover:text-violet-700 text-neutral-600 transition-colors"
                        >
                          [{tag}]
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              rows={10}
              className="w-full text-sm border border-neutral-200 rounded-lg px-3 py-2 bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 font-mono resize-y"
            />
            <div className="text-xs text-neutral-400 mt-1 text-right">{text.length} / 5000 chars</div>
          </div>

          {/* Parameters */}
          <div className="bg-white border border-neutral-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-neutral-700 flex items-center gap-1.5 mb-4">
              <Sliders className="h-4 w-4" />
              Parameters
            </h3>

            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              {/* Model */}
              <div>
                <label className="text-xs font-medium text-neutral-500 block mb-1">Model</label>
                <select
                  value={model}
                  onChange={e => setModel(e.target.value)}
                  className="w-full text-sm border border-neutral-200 rounded-lg px-2.5 py-2 bg-neutral-50"
                >
                  {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>

              {/* Output Format */}
              <div>
                <label className="text-xs font-medium text-neutral-500 block mb-1">Output Format</label>
                <select
                  value={outputFormat}
                  onChange={e => setOutputFormat(e.target.value)}
                  className="w-full text-sm border border-neutral-200 rounded-lg px-2.5 py-2 bg-neutral-50"
                >
                  {OUTPUT_FORMATS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>

              {/* Stability */}
              <div>
                <label className="text-xs font-medium text-neutral-500 block mb-1">
                  Stability: <span className="font-mono text-neutral-700">{stability.toFixed(2)}</span>
                </label>
                <input
                  type="range" min="0" max="1" step="0.01"
                  value={stability}
                  onChange={e => setStability(parseFloat(e.target.value))}
                  className="w-full accent-blue-600"
                />
                <div className="flex justify-between text-[10px] text-neutral-400 mt-0.5">
                  <span>Expressive</span><span>Stable</span>
                </div>
              </div>

              {/* Similarity Boost */}
              <div>
                <label className="text-xs font-medium text-neutral-500 block mb-1">
                  Similarity Boost: <span className="font-mono text-neutral-700">{similarityBoost.toFixed(2)}</span>
                </label>
                <input
                  type="range" min="0" max="1" step="0.01"
                  value={similarityBoost}
                  onChange={e => setSimilarityBoost(parseFloat(e.target.value))}
                  className="w-full accent-blue-600"
                />
                <div className="flex justify-between text-[10px] text-neutral-400 mt-0.5">
                  <span>Creative</span><span>Faithful</span>
                </div>
              </div>

              {/* Style */}
              <div>
                <label className="text-xs font-medium text-neutral-500 block mb-1">
                  Style: <span className="font-mono text-neutral-700">{style.toFixed(2)}</span>
                </label>
                <input
                  type="range" min="0" max="1" step="0.01"
                  value={style}
                  onChange={e => setStyle(parseFloat(e.target.value))}
                  className="w-full accent-blue-600"
                />
                <div className="flex justify-between text-[10px] text-neutral-400 mt-0.5">
                  <span>Neutral</span><span>Exaggerated</span>
                </div>
              </div>

              {/* Speed */}
              <div>
                <label className="text-xs font-medium text-neutral-500 block mb-1">
                  Speed: <span className="font-mono text-neutral-700">{speed.toFixed(2)}</span>
                </label>
                <input
                  type="range" min="0.7" max="1.2" step="0.01"
                  value={speed}
                  onChange={e => setSpeed(parseFloat(e.target.value))}
                  className="w-full accent-blue-600"
                />
                <div className="flex justify-between text-[10px] text-neutral-400 mt-0.5">
                  <span>0.7x</span><span>1.0x</span><span>1.2x</span>
                </div>
              </div>

              {/* Speaker Boost */}
              <div className="flex items-center gap-2 col-span-2">
                <input
                  type="checkbox"
                  id="speakerBoost"
                  checked={useSpeakerBoost}
                  onChange={e => setUseSpeakerBoost(e.target.checked)}
                  className="accent-blue-600"
                />
                <label htmlFor="speakerBoost" className="text-sm text-neutral-700">
                  Speaker Boost <span className="text-xs text-neutral-400">(enhances similarity, adds latency)</span>
                </label>
              </div>
            </div>

            <button
              onClick={handleGenerate}
              disabled={generating || !selectedVoice || !text.trim()}
              className="mt-4 w-full py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            >
              {generating ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
              ) : (
                <><Volume2 className="h-4 w-4" /> Generate</>
              )}
            </button>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="bg-white border border-neutral-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">
                Results ({results.length})
              </h3>
              <div className="space-y-2">
                {results.map((r, idx) => (
                  <div
                    key={r.timestamp}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors",
                      playingIdx === idx ? "bg-blue-50 border-blue-200" : "bg-neutral-50 border-neutral-100 hover:border-neutral-200"
                    )}
                  >
                    <button
                      onClick={() => playResult(idx)}
                      className="p-2 rounded-full bg-white border border-neutral-200 hover:bg-neutral-100 transition-colors shrink-0"
                    >
                      {playingIdx === idx ? <Square className="h-4 w-4 text-blue-600" /> : <Play className="h-4 w-4 text-neutral-700" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-neutral-800">{r.voiceName}</div>
                      <div className="text-[11px] text-neutral-400 font-mono truncate">
                        {r.params.model} · stab={r.params.stability} sim={r.params.similarityBoost} style={r.params.style} spd={r.params.speed} {r.params.useSpeakerBoost ? 'boost' : ''} · {r.params.outputFormat}
                      </div>
                    </div>
                    <span className="text-[10px] text-neutral-400 shrink-0">
                      {new Date(r.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
